import styles from '../PaymentModalTable.module.css';
import WrapperTable from 'ui/WrapperTable/WrapperTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import useNonNullContext from 'Hooks/useNonNullContext';
import StatusCell from '../TableCells/StatusCell/StatusCell';
import { RequestStatus } from '../helpers';
import { OutstandingPaymentsTableRow } from '../../OutstandingPayments/helpers';
import AccountCell from '../../TableCells/AccountCell/AccountCell';
import CostLabel from 'ui/DataDisplay/Money/CostLabel/CostLabel';
import ReleaseCell from '../../TableCells/ReleaseCell/ReleaseCell';
import PlatformCell from '../../TableCells/PlatformCell/PlatformCell';

type TableRow = OutstandingPaymentsTableRow & {
    paymentRequestStatus: RequestStatus | undefined;
};

type Props = {
    data: TableRow[];
    arePaymentsLoading: boolean;
};

type TableCellContext<K extends keyof TableRow> = CellContext<TableRow, TableRow[K]>;

const ManualPaymentsModalTable = ({ data, arePaymentsLoading }: Props) => {
    const { currencies } = useNonNullContext(OptionsContext);

    const columns: ColumnDef<TableRow, any>[] = [
        {
            header: 'Account',
            accessorKey: 'influencer',
            cell: ({ row: { original } }: TableCellContext<'influencer'>) => (
                <AccountCell
                    influencer={original.influencer || null}
                    influencerAccount={original.influencerAccount || null}
                    imageUrl={original.userImageUrl}
                    platform={original.platform}
                />
            ),
        },
        {
            header: 'Platform',
            accessorKey: 'platform',
            cell: ({ getValue }: TableCellContext<'platform'>) => <PlatformCell platform={getValue()} />,
        },
        {
            header: 'Release',
            accessorKey: 'release',
            cell: ({ getValue }: TableCellContext<'release'>) => <ReleaseCell release={getValue()} />,
        },
        {
            header: 'Cost',
            accessorKey: 'cost',
            cell: ({ getValue, row: { original } }: TableCellContext<'cost'>) => {
                const currency = currencies.find((currency) => currency.id === original.currency_id);
                return <CostLabel cost={getValue()} currencySymbol={currency?.symbol ?? ''} />;
            },
        },
        {
            header: 'Payment Details',
            accessorKey: 'payment_details',
            cell: ({ getValue }: TableCellContext<'payment_details'>) => <span>{getValue()}</span>,
        },
        {
            header: 'Status',
            accessorKey: 'paymentRequestStatus',
            cell: ({ getValue }: TableCellContext<'paymentRequestStatus'>) => (
                <StatusCell status={getValue()} isRequestLoading={arePaymentsLoading} />
            ),
        },
    ];

    return <WrapperTable data={data} columns={columns} className={styles.table} />;
};

export default ManualPaymentsModalTable;
